// Curated, plain-language patch notes for the in-app /changelog screen.
// Newest first. Keep entries short and user-facing — the full developer history is in CHANGELOG.md.
// When you ship a release, add an entry here (and to CHANGELOG.md, and bump package.json).
export const CHANGELOG = [
  {
    version: "2.89.0",
    title: "Find your next exercise, without losing your flow",
    notes: [
      "Tap Replace exercise during a session to find alternatives ranked by muscle and movement match, with technique details one tap away.",
      "Keep the same equipment, choose another type, or search the matching alternatives.",
      "Use the replacement just today or update the saved workout for next time too.",
      "Already logged sets stay with the original exercise. Remaining sets move across with a fresh weight field.",
    ],
  },
  {
    version: "2.88.0",
    title: "Active program highlights & modern frosted topbar",
    notes: [
      "Workouts in your active program are now badged and highlighted in the Start screen with program name and scheduled slot (e.g. Day 1: Push).",
      "Today's scheduled program workout is surfaced at the top of the templates browser with accent styling and instant session linking.",
      "Searching templates now matches program names and day labels alongside workout and exercise titles.",
      "The topbar across the app is modernized with frosted glass blur, category kickers, title icons, and status badge pills.",
      "Measurement logging adds landmark reference guides and precision stepper adjustments.",
    ],
  },
  {
    version: "2.87.1",
    title: "Account switching & body map hygiene",
    notes: [
      "Muscle map and body diagrams automatically switch between female and male models based on the logged-in user profile, with no manual re-saving required.",
      "Logging out or switching accounts now performs a full session cleanup, wiping stale active workout snapshots and un-synced offline data.",
      "Science coaching insight dismissals are now scoped per user account so one user's dismissed tips won't hide insights from another.",
    ],
  },
  {
    version: "2.87.0",
    title: "Visual weekly review & contextual training plans",
    notes: [
      "Weekly review (/week/:date) features an interactive front & back anatomical MuscleMap showing all primary and secondary muscles trained.",
      "Tapping any highlighted muscle opens its volume history and progression drill-down, complemented by working-set chips per muscle group.",
      "Exercise thumbnail images displayed beside each comparable lift and featured on the Home review preview card.",
      "Contextual weekly training plans with flexible goal setting, continuity preservation for paused weeks, and next-session planning shortcuts.",
      "Home screen displays Training Insights open by default to keep daily guidance and recovery front-and-center.",
    ],
  },
  {
    version: "2.86.0",
    title: "Training autonomy, intelligent recovery & body composition",
    notes: [
      "Quick stepper chips in the workout logger allow one-tap weight (±2.5/±5) and rep adjustments without keyboard typing.",
      "Rest timer now learns from your actual rest history per exercise, auto-pacing heavy compounds and accessories.",
      "Double progression support: set custom rep ranges (e.g. 8–12) with overload suggestions that advance reps first, then weight.",
      "1-tap barbell plate calculator icon in the exercise header and interactive Weight column header.",
      "Body composition intelligence: Navy tape body fat %, Fat-Free Mass Index (FFMI), and contextualized BMI on the Bodyweight page.",
      "Muscle recovery map adds a 5th detrained tier (14+ days) to highlight neglected muscle groups.",
      "Weekly Sunday training recap push notification summarizing completed sessions and total tonnage lifted.",
      "Segmented settings view divided into focused cards: Training & Gym, Body & Health, Notifications, and Account.",
      "Active workout bar now shows a live progress track and set count (e.g. 8 of 14 sets done) with monospace timer.",
    ],
  },
  {
    version: "2.85.0",
    title: "Today’s training, without the extra taps",
    notes: [
      "Home now puts this week’s goal in the hero, with records, bodyweight, and achievements in view instead of tucked behind a disclosure.",
      "Progress splits into Overview — a chart of real workouts you can tap through — and a workout log.",
      "Start a workout by searching templates, and in the logger a tap selects the number so you can type over it; Enter moves to the next field.",
      "Pinch-zoom works again.",
    ],
  },
  {
    version: "2.84.0",
    title: "A calmer look across the app",
    notes: [
      "Home now opens on the workout in front of you, with the rest of the day sitting underneath.",
      "On a phone the navigation floats; on a larger screen it sits as a sidebar so pages can use the width.",
      "Planning, progress, friends, and settings share the same headings, cards, and empty states.",
      "During a workout you can see how many working sets are done, and the how-to note folds away so it doesn't sit on the logger.",
    ],
  },
  {
    version: "2.83.0",
    title: "Complete a set with the volume button",
    notes: [
      "New in Settings → Workout controls: press volume down to tick off your current set and start the rest, without looking at the phone.",
      "Chalky hands, gloves, phone on the floor — it's the one control you can find by feel.",
      "Off by default. Only volume down is used, only while a workout is open, and the button goes straight back to normal the moment you leave it.",
      "Needs the new app version.",
    ],
  },
  {
    version: "2.82.0",
    title: "Change your rest from the lock screen",
    notes: [
      "The rest notification in the Android app now has buttons: “+30 s” while you're resting or once it's up, “Skip” to end the rest, and “Done” to dismiss.",
      "There's also a live countdown on your lock screen for the whole rest, so the phone in your pocket tells you where you are without unlocking it.",
      "Anything you change from the notification is carried back into your workout as soon as you open the app.",
      "Needs the new app version.",
    ],
  },
  {
    version: "2.81.0",
    title: "Train together",
    notes: [
      "If someone you follow (and who follows you back) is training at the same time, your workout offers to pair up.",
      "Once you both agree, a set finished by either of you starts the same rest on both phones — and each phone says whose set it was, so you always know whether it's your turn.",
      "Either of you can stop it at any point, and it ends by itself as soon as one of you finishes.",
    ],
  },
  {
    version: "2.80.0",
    title: "Your band can tell you when you're ready",
    notes: [
      "New in Settings → Health Connect: “Rest recovery hint”. With it on, your heart rate shows during rests and Kashtu says when it's back down.",
      "It's a suggestion beside the countdown, never a condition on it — your band only reports about once a minute, so a timer that waited on it could leave you standing around for no visible reason.",
      "Off by default, and per device: keeping the feed running through a lifting workout costs a little battery.",
    ],
  },
  {
    version: "2.79.0",
    title: "Your year in review",
    notes: [
      "Progress → Your year looks back over a whole year at once: total tonnage (anchored to something you can actually picture), your heaviest set, your best month, your longest streak and where the work went.",
      "It works with three months of history as happily as with three years — it tells you how much of the year it's speaking from instead of quietly making a short year sound like a long one.",
      "Pick any year you've trained in, and share the summary if you want to.",
    ],
  },
  {
    version: "2.78.0",
    title: "The bow finally looses its arrow",
    notes: [
      "Finishing a workout now resolves the Kashtu bow: it releases once, quietly, at the top of your summary.",
      "Short and restrained rather than a shower of confetti — and it never holds the summary up.",
      "If you've asked your device for reduced motion, you get the same resolved image with nothing moving.",
    ],
  },
  {
    version: "2.77.0",
    title: "Finish a workout without a signal",
    notes: [
      "You can now hit Finish with no connection — a dead spot in the gym no longer traps you in a workout you've already done.",
      "Kashtu tells you plainly that it's saved on your device and sends it the moment you're back online; you don't need to keep the app open.",
      "Until it lands, a bar across the app says so, so it never looks saved when it isn't. Records and achievements are worked out once it syncs.",
    ],
  },
  {
    version: "2.76.0",
    title: "See your heart rate through a workout",
    notes: [
      "Workouts recorded with your band now show a heart-rate curve on the workout's page in History.",
      "Each completed set is marked along it, so you can see which set really cost the most and how long your rests actually ran.",
      "Your band reports about once a minute, so it's the shape of the session rather than a beat-by-beat trace — and workouts recorded without a band simply don't show the card.",
    ],
  },
  {
    version: "2.75.0",
    title: "The rest screen now tells you what's next",
    notes: [
      "The big rest countdown in the Android app now has one line under it: the exercise, the weight and reps, and which set you're on.",
      "Glance at the phone on the bench and you know your next move without touching anything.",
      "It follows the workout — when an exercise finishes it points at the next one, and it updates if you log a set on another device.",
    ],
  },
  {
    version: "2.74.0",
    title: "A rest timer you can read across the gym",
    notes: [
      "In the Android app the screen no longer sleeps while you're resting.",
      "Put the phone down and the rest timer takes over the whole screen: a big countdown on black, dimmed right down — easy to read from the bench, and barely any battery on an OLED display.",
      "Tap anywhere to go back to the workout; it doesn't come back for the rest of that rest.",
    ],
  },
  {
    version: "2.73.0",
    title: "Comparable morning weigh-ins",
    notes: [
      "Set your usual wake-up time in Settings and weigh-ins within 3 hours either side are marked “first-morning, post-void” by default.",
      "The morning marker is always a checkbox, so you can correct it manually—and it can appear alongside the existing post-workout marker.",
      "Bodyweight history now shows the time each measurement was logged, not only the date.",
    ],
  },
  {
    version: "2.72.0",
    title: "One rest timer on every device",
    notes: [
      "Completing a set on one device now starts the same rest countdown on every open Kashtu logger.",
      "The Android app receives the shared deadline in the background and still alerts on time when the phone is locked.",
      "Set values logged on another device appear in the active workout automatically.",
      "Skipping or adjusting rest updates the timer across devices too.",
    ],
  },
  {
    version: "2.71.1",
    title: "More precise bodyweight tracking",
    notes: [
      "Bodyweight values now keep 0.05 kg precision, so a weigh-in such as 82.25 kg is displayed accurately instead of rounding to 82.5 kg.",
    ],
  },
  {
    version: "2.71.0",
    title: "Rest alerts that actually reach you",
    notes: [
      "The rest timer now counts real time, so it keeps running while you're in another app instead of appearing to freeze.",
      "In the Android app, your phone itself now fires the \"rest complete\" alert, so it arrives on time even with the screen off.",
      "Opening an exercise page mid-rest no longer cancels the countdown.",
      "Android app: notifications for friends' workouts and reminders now register automatically on launch — they were silently unreachable before, even with the settings switched on.",
      "The live logger recovers on its own from a hiccup while loading your session instead of stopping at an error.",
    ],
  },
  {
    version: "2.70.0",
    title: "See where each workout belongs",
    notes: [
      "Workout cards now show the active program they belong to and their scheduled day, so templates are easier to understand at a glance.",
      "When the same workout appears more than once in a program, the tag lists every slot—for example, “Days 1 & 4.”",
    ],
  },
  {
    version: "2.69.0",
    title: "Weight trends show real time",
    notes: [
      "Bodyweight measurements now sit on a true time-scaled axis, so short and long gaps between weigh-ins are immediately visible.",
      "The trend chart adds clear start, midpoint, and end dates with subtle gridlines while keeping goal and weight labels readable on phones.",
    ],
  },
  {
    version: "2.68.1",
    title: "Content first, fewer doors",
    notes: [
      "Plan now opens your workout templates immediately, with Workouts and Programs as a simple switch instead of a separate menu.",
      "Progress now opens your real history, consistency, and volume data; Profile opens your totals and settings, with compact shortcuts for related tools.",
      "Stylesheets now use the app version in their URL, preventing a fresh release from ever appearing with older cached styling.",
    ],
  },
  {
    version: "2.68.0",
    title: "Five clear places to go",
    notes: [
      "The bottom navigation is now Home, Plan, Start, Progress, and More — larger targets with the core training loop front and center.",
      "New Plan, Progress, and More hubs gather templates and programs, training and body trends, community, account settings, and tools without removing any existing feature.",
      "Home now shows one Coach insight and keeps secondary reports behind “More from your week”, so today’s workout, weekly status, and recovery are easier to scan.",
    ],
  },
  {
    version: "2.67.0",
    title: "A more dependable, polished Kashtu",
    notes: [
      "Health monitoring now reads real Kashtu data, with a deeper database integrity check running regularly so catalog or account corruption cannot hide behind a green status.",
      "Text actions such as “Why?”, “See all” and “Program” once again look like part of Kashtu instead of browser-default buttons.",
      "Workout controls now have clearer keyboard focus and accessible names, including the central Start button and exercise reorder menus.",
    ],
  },
  {
    version: "2.66.1",
    title: "Security hardening",
    notes: [
      "A full security review of the public-facing server: no vulnerabilities found, plus a few belt-and-braces hardenings — push notification endpoints are now validated so the server only ever talks to real browser push services, and oversized requests are rejected before they can strain the Pi.",
    ],
  },
  {
    version: "2.66.0",
    title: "Focus mode in the logger",
    notes: [
      "Finished exercises now collapse to a compact summary row (name + sets done), keeping your current exercise front and center. Tap a collapsed row any time to see or edit its sets.",
      "When you check off the last set of an exercise, the view glides to your next unfinished one — no scrolling past what's already done.",
      "Not your thing? The target icon in the session header switches focus mode off (per device), just like hiding the RPE column.",
    ],
  },
  {
    version: "2.65.0",
    title: "The dashboard tells you about today",
    notes: [
      "One line above the start button now sums up your day at a glance: how recovered your legs and upper body are, what your program says is due, and roughly how long it'll take based on your own average — \"Legs recovered, upper body hit yesterday — Upper A is due, ~50 min.\"",
    ],
  },
  {
    version: "2.64.0",
    title: "Tap any muscle on the recovery map",
    notes: [
      "The dashboard's recovery body map is now tappable: tap a muscle to open its own page with weekly hard sets charted against your volume landmarks (MEV/MAV/MRV) and a plain-language read on whether you're under-, well- or over-training it.",
      "The muscle page also lists every exercise that hit it in the last 8 weeks — with set counts, primary/secondary role and when you last did each — and links straight to those exercises.",
    ],
  },
  {
    version: "2.63.0",
    title: "See the next strength level coming",
    notes: [
      "Exercise pages for recognized lifts (squat, bench, deadlift, rows, presses and their dumbbell variants) now draw the strength-standard bands right on your 1RM trend chart — you can watch your line climb toward Intermediate or Advanced.",
      "A callout under the chart tells you exactly how far the next band is: your current level, your lift-to-bodyweight ratio, and \"17 kg to Intermediate\".",
      "Accessories and unilateral lifts aren't graded (there are no honest standards for them) — they keep their plain trend chart.",
    ],
  },
  {
    version: "2.62.0",
    title: "Kashtu looks like Kashtu",
    notes: [
      "New visual identity: the generic indigo makes way for a deep Mesopotamian lapis blue with bronze accents — Kashtu is Akkadian for \"bow\", and the colors now nod to where the name comes from. Both light and dark themes, plus a refreshed app icon.",
      "The weekly-goal card now shows a drawn bow: it pulls back as your sessions accumulate and looses its arrow the moment you hit your goal.",
      "Streak flames and personal-record badges glow bronze instead of borrowing the warning color.",
    ],
  },
  {
    version: "2.61.0",
    title: "A sharper dashboard",
    notes: [
      "Week streak and weekly goal are now one card: a progress ring shows how far into this week's goal you are, with a line telling you exactly how many sessions keep (or start) your streak.",
      "Achievement badges swapped their emoji for crisp icons that match the rest of the app, in color when earned and grey while locked.",
      "Numbers across stats, badges and dates now use fixed-width digits, so timers and counts don't wobble as they change.",
    ],
  },
  {
    version: "2.60.0",
    title: "Your program follows you to the Start screen",
    notes: [
      "The Start screen now leads with today's program workout, just like the dashboard — starting from either place keeps your program on track. Repeat-last and templates are still right below.",
      "Every tappable card in the app now works with a keyboard too (Tab to focus, Enter to open) — groundwork for accessibility.",
    ],
  },
  {
    version: "2.59.0",
    title: "Fairer, clearer strength levels",
    notes: [
      "The bottom strength band is now called \"Untrained\" instead of \"Novice\" — popular standards sites use Novice for a band above Beginner, so our old name meant the opposite of what you'd expect elsewhere.",
      "Women's strength thresholds now scale per lift instead of one flat factor: the male–female gap is bigger in pressing than in squats and deadlifts, so bench and overhead press bands were graded too harshly before. Squat and deadlift levels may read slightly lower, pressing levels higher — they're fairer now (source cited on the Standards page).",
      "Muscles that are mostly trained through compound lifts (abs, traps, neck) now say \"Covered indirectly\" on the volume card instead of claiming a couple of incidental sets put them in the \"growth zone\".",
    ],
  },
  {
    version: "2.58.0",
    title: "The logger celebrates with you",
    notes: [
      "The weight suggestion above your sets (\"Hit 10 reps last time — add weight\") is now tappable: one tap fills every remaining set with the suggested weight and reps.",
      "Beat your estimated 1RM mid-workout and you'll know immediately — a record toast (and a buzz on your phone) fires the moment the set is checked off, not just in the end-of-workout summary.",
      "Checking off a set now gives a small vibration tick on supported phones.",
    ],
  },
  {
    version: "2.57.0",
    title: "Your program day front and center",
    notes: [
      "On a program day, the big dashboard button now starts today's workout directly — no more scrolling past the Coach to find it. Freestyle is one tap away via \"or start something else\".",
      "Today's program card sits right under your stats as a preview — tap it to see the exercises before you commit.",
      "The Coach now bundles related volume tips into one: instead of three separate \"below its weekly minimum\" notes, you get a single tip listing every muscle that needs a few more sets. Same for program-audit tips.",
    ],
  },
  {
    version: "2.56.1",
    title: "Little fixes everywhere",
    notes: [
      "A week streak of zero now actually shows the 0 instead of a lonely flame.",
      "The Coach no longer says \"1 hard sets\".",
      "About 20 exercise descriptions had garbled characters (like \"Â¾\") — all repaired.",
      "The History volume chart now shows its scale and date range, and says how many sessions it covers.",
      "Scrolled content no longer shows through the page heading.",
    ],
  },
  {
    version: "2.56.0",
    title: "Import a whole program from a document",
    notes: [
      "The Import screen can now read a written plan — paste text or pick a Markdown file — and turn it into workouts and a full program in one go. It understands Kashtu's own exported documents and anything written in the same shape (day headings, numbered exercises, Target/Rest lines), so you can ask an AI for a program, get one from a coach, or write your own.",
      "Exercise names are matched against the library automatically, including close-but-not-identical names. Anything it can't place is highlighted — tap it to pick from suggestions, search the library, or skip it. Nothing is created until every exercise is resolved.",
      "Supersets, rep ranges, rest times, and per-exercise notes in the document all carry over.",
      "Find it under Workouts → Import, or the new import button on the Programs screen.",
    ],
  },
  {
    version: "2.55.0",
    title: "A calmer Coach and honest 1RM estimates",
    notes: [
      "Coach tips are now one-line headlines you tap to expand - the most urgent tip stays open, and tips based on your actual training rank above the general how-to guidance.",
      "Estimated 1RMs now cap the rep count at 10: high-rep sets no longer inflate your PR board and strength levels (1RM formulas aren't calibrated past ~10 reps). Some records may read a little lower - they're more honest now.",
      "A light training week gets one gentle note on the History volume card instead of a warning under every muscle.",
      "New setting: hide the RPE column in the logger if you don't track effort (Profile - Settings - Preferences).",
    ],
  },
  {
    version: "2.54.0",
    title: "Your exercise notes carry into future sessions",
    notes: [
      "Leave a note on an exercise mid-workout — “right shoulder sore, dropped the weight” — and next time that exercise comes up you'll see it flagged at the top of the card, with the date, so a niggle you spotted last time doesn't get forgotten.",
      "It shows even if you skipped the exercise entirely last time, and sits alongside your usual “Last time” set stats and weight suggestion — those are unchanged.",
    ],
  },
  {
    version: "2.53.0",
    title: "Preview today's exercises before you start",
    notes: [
      "Your program's workout on the Home screen is now tappable: tap the card to expand a preview of every exercise for the day — with its sets and rep range — before you hit Start. Tap again to collapse it.",
    ],
  },
  {
    version: "2.52.0",
    title: "See how a program covers each muscle",
    notes: [
      "Open any program's menu and tap “Muscle coverage” to see the weekly hard sets each muscle would get if you did every workout as planned — laid out against the same MEV → MAV → MRV growth bands as your History card, so you can spot under- and over-trained muscles before you start.",
      "The Program editor now shows the same projection live as you build the schedule: add, remove or reorder days and the per-muscle coverage updates instantly. Programs longer than a week are scaled to a 7-day week so the numbers are comparable.",
    ],
  },
  {
    version: "2.51.1",
    title: "Dumbbell strength levels fixed",
    notes: [
      "Dumbbell lifts are now graded on the total weight of both dumbbells (the way you log them) instead of one dumbbell — so your dumbbell bench, incline and shoulder-press levels read correctly rather than about twice as high.",
      "Single-arm dumbbell presses are no longer graded against the two-arm standards, so they can't skew your overall level.",
    ],
  },
  {
    version: "2.51.0",
    title: "Strength standards reference",
    notes: [
      "New “How levels work” page (Dashboard → Personal records → How levels work): see exactly how your Novice→Elite level is derived, with the bodyweight-ratio bands for each main lift linked to the exercise, and your own current ratio + band shown beside each.",
      "Common variants are now scored on their own standards — front squat, dumbbell bench, incline dumbbell press, dumbbell shoulder press — instead of being judged against the barbell numbers. Accessory lifts (Romanian deadlift, split squat, machine/one-arm variants) no longer skew your level.",
      "Answers whether age, height and bodyweight matter (bodyweight is built in; age is noted but the numbers stay age-neutral; height is a caveat), with a cited source for every standard.",
    ],
  },
  {
    version: "2.50.0",
    title: "A smarter Coach: your level and your program",
    notes: [
      "Your Coach now estimates your training level from your main lifts (relative to your bodyweight) and recommends how much weekly volume to aim for — and roughly how many training days that means. So newer lifters aren't pushed to over-train, and stronger lifters get nudged when they're doing too little.",
      "It also reviews your active program directly: it flags muscles your plan doesn't train enough each week (below their minimum effective volume), any it over-does, and any whose sets are crammed into a single day instead of spread across two.",
      "As always, tap “Why?” on any tip to read the research behind it — it's general evidence-based guidance from your own logged training, not medical advice.",
    ],
  },
  {
    version: "2.49.0",
    title: "Export your plans, and fix exercise details",
    notes: [
      "Export any workout, program, or exercise as a clean document — open its menu and tap “Export document”. Copy it or download a .md file to share with a coach or paste into an LLM for feedback.",
      "Optionally tick “Include recent performance” when exporting to add your latest sets and estimated 1RM for each exercise, so the feedback can account for what you're actually lifting.",
      "Spotted wrong muscles or details on a library exercise? Open it and tap Edit. You can fix exercises you created; admins can correct any of the built-in catalog exercises too.",
    ],
  },
  {
    version: "2.48.0",
    title: "See last session's RPE",
    notes: [
      "The “Last time:” line under each exercise in the logger now shows the effort (RPE) you logged for each set, e.g. “100kg×5 @8”. Handy for judging whether to push harder this time.",
      "Your RPE is still never pre-filled into the new session — it's an in-the-moment rating, not a target, so each set starts blank. Only weight and reps carry over.",
    ],
  },
  {
    version: "2.47.1",
    title: "Two fixes",
    notes: [
      "Bodyweight exercises (like pushups or bench dips, logged with no added weight) are no longer wrongly flagged as “stalled” by your Coach — progress on those is about reps, not estimated 1RM.",
      "Fixed a hang in the Android app where the workout session screen could get stuck on “Loading your session…”. If a session ever fails to load now, you'll get a retry button instead of an endless spinner.",
    ],
  },
  {
    version: "2.47.0",
    title: "Coaching for your training goal",
    notes: [
      "Set a training goal — strength, muscle growth, endurance or general fitness — with an optional secondary focus (e.g. mainly muscle, with some strength). Pick it during setup or under Profile → Settings → Training goal.",
      "Your Coach now tailors its tips to that goal: the right rep ranges, rest times and intensity, plus which quality to train first when you're chasing two.",
      "Every workout now shows a quick “How to run this” tip — for strength and muscle that means straight sets (finish all sets of an exercise, resting between, before the next) rather than circuiting one set of each. Tap × to hide it.",
    ],
  },
  {
    version: "2.46.0",
    title: "Notifications in the Android app",
    notes: [
      "The sideloaded Android app now supports notifications — reminders, friend activity and the rest of it work just like they do on the web, even when the app is closed.",
      "Turn them on under Profile → Settings → Notifications. (The web/PWA is unchanged.)",
    ],
  },
  {
    version: "2.44.0",
    title: "Heart rate & distance for cardio",
    notes: [
      "Cardio workouts now show distance, pace and heart rate at a glance — in your History and on each session's detail page.",
      "Groundwork landed for importing Mi Band 9 Pro runs and heart rate from Android Health Connect. That lights up in the upcoming sideloaded Android app; the web app is unchanged until you connect a band.",
    ],
  },
  {
    version: "2.43.0",
    title: "Your Coach now knows you better",
    notes: [
      "Coach tips now factor in your bodyweight, age and sex — starting with a daily protein target worked out from your weight (and bumped up automatically when you're in a cut).",
      "If you're 50 or older, you'll also see a per-meal protein note; women in their late 40s to mid-50s get a tailored note about training through perimenopause.",
      "As always, every tip is cited under “Why?”, conservative, and dismissible.",
    ],
  },
  {
    version: "2.42.0",
    title: "Meet your Coach",
    notes: [
      "Your Home screen now has a Coach card with science-backed tips drawn from your own logged training — like a muscle that's below its weekly growth volume, a lift that's stalled and could use a deload, or a fat-loss pace that's a touch fast.",
      "Every tip has a “Why?” link that shows the research behind it, with links to the sources. It's general evidence-based guidance, not medical advice.",
      "Tips are tailored, conservative, and dismissible — tap the × on any you don't want to see.",
    ],
  },
  {
    version: "2.41.0",
    title: "Add your birth year (optional)",
    notes: [
      "Pop your birth year into onboarding or Settings and Kashtu can tune cardio heart-rate zones to your age — groundwork for the upcoming coaching tips.",
      "Your age now always shows correctly (worked out from your birth year) instead of a number that slowly drifted out of date.",
    ],
  },
  {
    version: "2.40.0",
    title: "Add sets & exercises even with no signal",
    notes: [
      "You can now add sets — and whole exercises — while offline. They show up instantly and sync automatically the moment you're back online, just like edits already did. No more 'reconnect to add sets' in a basement gym.",
      "The exercise list is now cached on your device, so the picker still opens with no connection.",
      "Faster first load: the app now ships as a single optimised bundle instead of dozens of separate files.",
    ],
  },
  {
    version: "2.39.0",
    title: "Shared workouts keep their cardio targets",
    notes: [
      "Share a workout that has cardio or stretching exercises and its target times and distances now travel with the share code — they no longer arrive blank.",
      "Under the hood: more automated tests around live logging, offline sync and reordering, so edits keep saving reliably.",
    ],
  },
  {
    version: "2.38.0",
    title: "Set up your whole profile in one go",
    notes: [
      "Onboarding now walks you through the essentials: units, sex, weekly goal, your weight & height, a first body measurement (with how-to tips), plus notification and privacy preferences — all optional and skippable.",
      "Add your height and the Bodyweight screen now shows your BMI.",
      "Fixed the dumbbell icon, which had been rendering as a broken squiggle.",
    ],
  },
  {
    version: "2.37.0",
    title: "A guided welcome",
    notes: [
      "New accounts now get a quick walkthrough: set your units, weekly goal and body map, then a short tour of how to log, plan and track your training.",
      "It ends with one-tap shortcuts to start your first workout, build a template, or browse the library.",
      "Want to see it again? Settings → Help → Replay setup & tour.",
    ],
  },
  {
    version: "2.36.0",
    title: "Your volume coach: MEV, MAV & MRV explained",
    notes: [
      "The weekly-sets card now coaches you: each muscle is scored against its own growth landmarks, with a tap-to-learn guide that explains every term (MEV, MAV, MRV) in plain English.",
      "Only sets taken near failure (RPE 7+) count as 'hard sets', and helper muscles (like triceps on a bench press) now count as half a set — a truer picture of real stimulus.",
      "Cardio and stretching exercises in your templates can now carry a target time and distance, which prefill when you start the workout.",
    ],
  },
  {
    version: "2.35.0",
    title: "Measurements, made easy",
    notes: [
      "Tap a body part on the new silhouette to log it — no more dropdown picking.",
      "Set a target for any measurement and watch a progress bar fill toward it.",
      "A new Proportions card shows your waist-to-hip and shoulder-to-waist ratios with healthy/ideal guides.",
    ],
  },
  {
    version: "2.34.0",
    title: "Body measurements, strength levels & library filters",
    notes: [
      "Track body-part measurements (waist, arms, chest…) over time on the new Measurements screen.",
      "Your big lifts now show a Novice→Elite level next to the bodyweight-ratio chip.",
      "Filter the exercise library by type — cardio, stretching and more.",
    ],
  },
  {
    version: "2.33.0",
    title: "Weekly sets per muscle",
    notes: [
      "A new History card shows your hard sets per muscle each week against growth targets — the metric that actually drives muscle gain.",
    ],
  },
  {
    version: "2.32.0",
    title: "Cardio & flexibility tracking",
    notes: [
      "Log runs, rows and rides by distance + time, and see your pace.",
      "Log stretches and timed holds by duration. Cardio and mobility are now first-class alongside lifting.",
    ],
  },
  {
    version: "2.31.0",
    title: "Invite codes",
    notes: [
      "Invite someone with a single-use code or link.",
    ],
  },
  {
    version: "2.30.0",
    title: "Privacy, Terms & invite-only",
    notes: [
      "Added a Privacy Policy and Terms of Service, linked from your profile.",
      "Kashtu is invite-only for now.",
    ],
  },
  {
    version: "2.28.0",
    title: "First-run setup",
    notes: [
      "New accounts get a quick 3-step setup for units, weekly goal and body diagram.",
    ],
  },
  {
    version: "2.27.0",
    title: "Report content",
    notes: [
      "You can now report a workout, program or user that breaks the rules.",
    ],
  },
  {
    version: "2.26.0",
    title: "Meet Kashtu 🏹",
    notes: [
      "A new name, logo and refreshed look — the app is now Kashtu.",
    ],
  },
  {
    version: "2.25.0",
    title: "Account security",
    notes: [
      "Reset a forgotten password by email, and confirm your email address.",
    ],
  },
  {
    version: "2.24.0",
    title: "Supersets & deeper analytics",
    notes: [
      "Group exercises into supersets in the builder and logger.",
      "New volume-by-muscle chart, plus a bodyweight-ratio on your personal records.",
      "Tap the RPE column header for a quick explainer of the scale.",
    ],
  },
  {
    version: "2.23.0",
    title: "Photos & demo videos for your exercises",
    notes: [
      "Add a photo and a demo-video link to exercises you create.",
    ],
  },
  {
    version: "2.22.0",
    title: "Create your own exercises",
    notes: [
      "Add custom exercises to the library, with muscles and instructions.",
    ],
  },
  {
    version: "2.19.0",
    title: "Smarter muscle map",
    notes: [
      "The recovery map now shows muscles you work indirectly, and the workout wizard helps you find gaps.",
    ],
  },
  {
    version: "2.18.0",
    title: "Calendar-based programs",
    notes: [
      "Programs can now follow the real calendar and repeat weekly, with rest days that advance on their own.",
      "Fixed RPE not saving when you tapped the ✓ to complete a set.",
    ],
  },
  {
    version: "2.17.0",
    title: "Plate calculator & consistency heatmap",
    notes: [
      "A plate calculator in the logger works out which plates to load per side.",
      "A GitHub-style consistency heatmap on History shows your training streaks at a glance.",
    ],
  },
  {
    version: "2.16.0",
    title: "Progress photos",
    notes: [
      "Capture progress photos with framing guides and an onion-skin overlay of your last shot. Photos stay on your device.",
    ],
  },
  {
    version: "2.14.0",
    title: "Shareable programs",
    notes: [
      "Publish multi-week programs to Discover, and enroll in programs others have shared.",
    ],
  },
  {
    version: "2.13.0",
    title: "Follow people",
    notes: [
      "Follow coaches and friends to see their workouts and activity. Mutual follows are your 'friends'.",
    ],
  },
  {
    version: "2.12.0",
    title: "Multi-week programs",
    notes: [
      "Build multi-week training programs that advance day-by-day and cycle automatically.",
    ],
  },
  {
    version: "2.11.0",
    title: "Offline logging",
    notes: [
      "Keep logging without a signal — your edits sync automatically when you reconnect.",
    ],
  },
  {
    version: "2.10.0",
    title: "Coaching & reliability",
    notes: [
      "The logger now suggests your next weight or reps based on last time.",
      "Faster and more secure under the hood.",
    ],
  },
];
